import { MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import type { CardProps } from '@designsystem/stories/03-molecules/Card';
import { Button } from '@designsystem/stories/02-atoms/Button';
import { Listing } from '@designsystem/stories/04-organisms/Listing';
import { messages } from '../messages';

interface OccasionsListingProps {
  occasions: CardProps[];
  hasMore: boolean;
  onShowMore: () => void;
}

type RegistrationAction = NonNullable<CardProps['actions']>[number] & {
  'data-analytics-occasion-id'?: string | boolean;
};

const hasRegistrationClass = (action: RegistrationAction) =>
  action.className?.split(/\s+/).includes('ga-df-anmeldung') === true;

/**
 * Component for occasions listing with pagination
 * Shows occasions as card list with "Show more" button
 */
export const OccasionsListing = ({
  occasions,
  hasMore,
  onShowMore,
}: OccasionsListingProps) => {
  const intl = useIntl();

  if (occasions.length === 0) {
    return null;
  }

  const onRegistrationClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const registrationLink = target.closest<HTMLElement>('.ga-df-anmeldung');
    if (!registrationLink) {
      return;
    }

    const analyticsOccasionId = registrationLink.dataset.analyticsOccasionId;
    const href = registrationLink.getAttribute('href');
    const registrationAction = occasions
      .flatMap((occasion) => occasion.actions || [])
      .find((action) => {
        const candidate = action as RegistrationAction;
        if (!hasRegistrationClass(candidate)) {
          return false;
        }
        if (analyticsOccasionId) {
          return (
            candidate['data-analytics-occasion-id'] === analyticsOccasionId
          );
        }
        return !!href && candidate.href === href;
      });

    // Some rendered registration links navigate before the regular action
    // onClick path runs. Capture keeps analytics attached to the actual click;
    // pushRegistrationAnalytics deduplicates if both paths fire.
    registrationAction?.onClick?.(event as any);
  };

  return (
    <>
      <Listing
        type="register"
        items={occasions}
        onClickCapture={onRegistrationClickCapture}
      />
      {hasMore && (
        <p>
          <Button
            label={intl.formatMessage(messages.ShowMore)}
            type="secondary"
            onClick={onShowMore}
          />
        </p>
      )}
    </>
  );
};
